import React from "react";

const LegalLayout = ({ title, updated, children }) => (
  <div>
    <div className="bg-ep-royal py-14">
      <div className="max-w-4xl mx-auto px-5 lg:px-8">
        <div className="text-[#ff5a5a] font-semibold tracking-widest text-xs uppercase">Legal</div>
        <h1 className="text-white text-3xl sm:text-4xl font-bold mt-2">{title}</h1>
        <p className="text-white/60 text-sm mt-2">Last updated: {updated}</p>
      </div>
    </div>
    <div className="bg-white">
      <div className="max-w-4xl mx-auto px-5 lg:px-8 py-14 prose-legal">
        {children}
      </div>
    </div>
  </div>
);

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="text-[#0a1a4a] text-xl font-bold mb-3">{title}</h2>
    <div className="text-[#0a1a4a]/80 text-sm leading-relaxed space-y-3">{children}</div>
  </section>
);

export const Terms = () => (
  <LegalLayout title="Terms & Conditions" updated="July 2025">
    <Section title="1. Acceptance of Terms">
      <p>
        These Terms &amp; Conditions govern your access to and use of the
        Explorer Picks website and any content, features, or services made
        available through it. By accessing this site or subscribing to any
        membership plan, you agree to be bound by these Terms in full. If you
        do not accept them, you must discontinue use of the platform
        immediately.
      </p>
    </Section>
    <Section title="2. Eligibility">
      <p>
        You must be at least eighteen (18) years of age, or of the legal
        gambling age in your jurisdiction, whichever is greater. It is your
        sole responsibility to verify that placing bets on football matches
        is lawful where you reside. Explorer Picks does not accept liability
        for members who access the service from jurisdictions where such
        activity is restricted.
      </p>
    </Section>
    <Section title="3. Nature of the Service">
      <p>
        Explorer Picks provides opinion-based sports predictions and analysis
        as an information product. We do not operate a bookmaker, do not
        accept wagers, and do not process bets on behalf of any user. All
        selections published are the personal opinions of our analysts and
        should be treated as informational content only.
      </p>
    </Section>
    <Section title="4. Membership & Payments">
      <p>
        Access to certain content is restricted to paid members. Fees for
        each plan are displayed on the Membership page and are quoted in the
        currency shown. Because our product is delivered digitally and is
        time-sensitive, all fees are non-refundable once picks have been
        delivered, except where required by applicable consumer-protection
        law.
      </p>
    </Section>
    <Section title="5. Intellectual Property">
      <p>
        All content on this website — including text, graphics, logos,
        layouts, and picks archives — is the property of Explorer Picks or
        its licensors and is protected by copyright and other intellectual
        property laws. You may not reproduce, redistribute, resell, or
        publicly display any portion of the service without our prior
        written consent.
      </p>
    </Section>
    <Section title="6. Prohibited Conduct">
      <p>
        You agree not to (a) share your account credentials or picks with
        third parties, (b) attempt to interfere with the security or
        availability of the service, or (c) use the site in a manner that
        violates any applicable law. Breach of these obligations may result
        in immediate termination of your account without refund.
      </p>
    </Section>
    <Section title="7. Limitation of Liability">
      <p>
        To the fullest extent permitted by law, Explorer Picks and its
        contributors shall not be liable for any direct, indirect,
        incidental, or consequential losses, including but not limited to
        losses arising from bets placed based on our published picks.
      </p>
    </Section>
    <Section title="8. Modifications">
      <p>
        We may amend these Terms from time to time. The most current version
        will always be available on this page and will supersede all
        previous versions. Continued use of the service after changes are
        posted constitutes acceptance of the revised Terms.
      </p>
    </Section>
  </LegalLayout>
);

export const Privacy = () => (
  <LegalLayout title="Privacy Policy" updated="July 2025">
    <Section title="1. Introduction">
      <p>
        Explorer Picks (“we”, “our”, “us”) respects your privacy and is
        committed to protecting the personal data we collect from members
        and visitors. This Privacy Policy explains what information we
        gather, how it is used, and the rights you have in relation to it.
      </p>
    </Section>
    <Section title="2. Data We Collect">
      <p>
        We collect information that you voluntarily provide when you
        subscribe to a plan or contact us — typically your name, email
        address, and payment reference. We also collect limited technical
        data automatically, such as your browser type and general usage
        statistics, in order to keep the site secure and improve the member
        experience.
      </p>
    </Section>
    <Section title="3. How We Use Your Data">
      <p>
        Personal data is used solely for delivering our services: to send
        you the picks you have subscribed to, to respond to your
        enquiries, to manage payments, and to comply with our legal
        obligations. We do not sell your data and we do not share it with
        third parties for marketing purposes.
      </p>
    </Section>
    <Section title="4. Payment Processing">
      <p>
        Payments are processed by regulated third-party providers. When you
        pay for a membership, your card or wallet details are handled
        directly by that provider under their own privacy and security
        policies. Explorer Picks never stores full card numbers on its
        servers.
      </p>
    </Section>
    <Section title="5. Cookies">
      <p>
        Our website uses a small number of cookies to keep sessions active
        and to measure aggregated usage. You may disable cookies through
        your browser settings, but some features of the site may not work
        correctly without them.
      </p>
    </Section>
    <Section title="6. Data Retention">
      <p>
        We keep personal data only for as long as necessary to fulfil the
        purpose it was collected for or to comply with tax, accounting, and
        legal requirements. When retention is no longer required, data is
        securely deleted.
      </p>
    </Section>
    <Section title="7. Your Rights">
      <p>
        Depending on where you reside, you may have the right to access,
        rectify, or erase your personal data, to restrict or object to its
        processing, and to request portability. To exercise any of these
        rights, please contact us at the email address listed on our
        Contact page.
      </p>
    </Section>
    <Section title="8. Security">
      <p>
        We apply reasonable technical and organisational measures to
        safeguard your personal data against unauthorised access, alteration,
        or destruction. However, no method of transmission over the
        internet is entirely secure and we cannot guarantee absolute
        security.
      </p>
    </Section>
    <Section title="9. Changes to this Policy">
      <p>
        We may update this Privacy Policy periodically. The date of the
        latest revision is shown at the top of this page.
      </p>
    </Section>
  </LegalLayout>
);

export const Disclaimer = () => (
  <LegalLayout title="Disclaimer" updated="July 2025">
    <Section title="1. Informational Purpose">
      <p>
        All content published on the Explorer Picks website — including but
        not limited to picks, statistics, analyses, and archived results —
        is provided for informational and entertainment purposes only. It
        does not constitute financial advice, investment advice, or a
        recommendation to place a wager.
      </p>
    </Section>
    <Section title="2. No Guarantee of Results">
      <p>
        Sports betting outcomes are influenced by numerous unpredictable
        variables. Historical performance shown on this website is not a
        promise or guarantee of future results, and no representation is
        made that any member will achieve profits similar to those
        illustrated in our archive.
      </p>
    </Section>
    <Section title="3. Personal Responsibility">
      <p>
        Any decision to place a bet based on information published on this
        site is entirely your own responsibility. You are solely accountable
        for the funds you risk, and Explorer Picks accepts no liability for
        losses — direct or indirect — arising from your betting activity.
      </p>
    </Section>
    <Section title="4. Responsible Gambling">
      <p>
        We strongly encourage responsible gambling. Only stake amounts you
        can comfortably afford to lose, set personal limits, and step away
        when needed. If you feel that gambling is affecting your wellbeing,
        please seek help from a qualified support organisation such as
        GambleAware, GamCare, or the equivalent authority in your
        jurisdiction.
      </p>
    </Section>
    <Section title="5. Age Restriction">
      <p>
        Explorer Picks content is intended exclusively for adults of legal
        gambling age. If you are below that age, you must leave the website
        immediately.
      </p>
    </Section>
    <Section title="6. Third-Party Links & Bookmakers">
      <p>
        We may reference third-party bookmakers, tools, or resources for
        convenience. Explorer Picks is not affiliated with, does not
        endorse, and takes no responsibility for the content, terms, or
        services provided by those third parties.
      </p>
    </Section>
  </LegalLayout>
);

export default Terms;
